import { isCH, isDK, isSE, type LocaleIso } from '../i18n';
import {
  VOUCHER_VARIANTS,
  type CampaignParams,
  type VoucherVariant,
  type CampaignServiceEndpoint,
  type CampaignVoucherDetails
} from './types';

/**
 * Maps pref string prefixes to voucher variant names.
 * Extracted from first two underscore-separated parts of pref parameter.
 *
 * Examples:
 * - 'cpc_gg_pid_...' → 'cpc_gg' → 'google'
 * - 'cpc_bing_...' → 'cpc_bing' → 'bing'
 * - 'cpc_brave_...' → 'cpc_brave' → 'google'
 * - 'cpl_apr_...' → 'cpl_apr' → 'campaign_partner'
 *
 * Based on PREF_TO_VARIANT from Gatsby voucher.js
 */
const CAMPAIGN_PREF_TO_VARIANT: Record<string, VoucherVariant> = {
  cpc_gg: VOUCHER_VARIANTS.GOOGLE,
  // Brave and ChatGPT SEA campaigns share the Google voucher
  cpc_brave: VOUCHER_VARIANTS.GOOGLE,
  cpc_oai: VOUCHER_VARIANTS.GOOGLE,
  cpc_bing: VOUCHER_VARIANTS.BING,
  cpc_ac: VOUCHER_VARIANTS.BING,
  cpl_apr: VOUCHER_VARIANTS.CAMPAIGN_PARTNER
};

/**
 * Maps voucher variants to their default email template IDs.
 * Used when campaign ID is not provided or not found in CAMPAIGN_EMAIL_ID_TO_CAMPAIGNS.
 *
 * Based on VARIANT_TO_EMAIL_ID from Gatsby voucher.js
 */
const CAMPAIGN_VARIANT_TO_EMAIL_ID: Record<VoucherVariant, number> = {
  [VOUCHER_VARIANTS.GOOGLE]: 215,
  [VOUCHER_VARIANTS.BING]: 215,
  [VOUCHER_VARIANTS.CAMPAIGN_PARTNER]: 251,
  [VOUCHER_VARIANTS.OTHERS]: 217
};

/**
 * Maps email template IDs to their associated campaign IDs.
 * Used to determine which email template to use based on campaign ID from URL.
 *
 * Email template IDs:
 * - 215: Standard Google/Bing campaigns (most common)
 * - 217: Default/standard template (fallback)
 * - 251: Campaign partner emails (special service endpoint)
 * - 392: Specific campaign with unique template
 *
 * Based on EMAIL_ID_TO_CAMPAIGNS from Gatsby voucher.js
 */
const CAMPAIGN_EMAIL_ID_TO_CAMPAIGNS: Record<number, number[]> = {
  215: [
    58437274, 677626760, 14126909273, 198799114, 190186395, 190327025, 190327024, 190186428, 399455469, 190187433,
    265108431, 190187405, 399456635, 190326961, 190326851, 399268217, 1358107802, 24059734, 24086284, 33415954,
    1358107679, 24067654, 68094634, 13817974, 16816385891, 23453674, 7113260877, 36954664, 22533394, 34619614,
    1500779162, 22532914, 620208226, 19761518826
  ],
  392: [13918917553]
};

/**
 * Maps email template IDs to their voucher configuration.
 * Contains voucher value and PSI service endpoint for each template.
 *
 * Voucher values are locale-specific:
 * - Email ID 215: CH=25, DK=20%, SE=30%, default=15
 * - Email ID 392: 15 (all locales)
 * - Email ID 217: Free shipping voucher for purchases over 15 euro
 * - Email ID 251: 15 euro voucher (sent by default for campaign partner)
 *
 * ARCHITECTURE NOTE - Voucher Value Handling:
 * Email templates handle voucher values in two ways:
 *
 * 1. DYNAMIC (code-based) - Templates 215, 392:
 *    - Voucher value passed from frontend → PSI service → email template
 *    - Template uses {{vcode}} placeholder to display dynamic value
 *    - getValue() returns the voucher amount (15, "20%", etc.)
 *
 * 2. STATIC (template-based) - Templates 217, 251:
 *    - Voucher value managed by email template itself (not passed from frontend)
 *    - Frontend passes NO voucher value (getValue: undefined)
 *    - Email template manages its own voucher content
 *    - Allows marketing team to update values without code changes
 *
 * Template 217 (no p_ref / unknown):
 * - getValue returns undefined (no dynamic value passed)
 * - Free shipping voucher is sent for purchases over 15 euro
 *
 * Template 251 (campaign_partner):
 * - getValue returns undefined (no dynamic value passed)
 * - 15 euro voucher is sent by default for the campaign
 * - This design allows campaign-specific customization via email template editor
 *
 * Verified against:
 * - Gatsby implementation: website/packages/core/cms/helpers/pref/voucher.js
 * - PHP PSI service: php/src/Pixum/Email/Service/EmailServiceBridge.php
 *
 * Based on EMAIL_ID_TO_VOUCHER from Gatsby voucher.js
 */
const CAMPAIGN_EMAIL_ID_TO_VOUCHER: Record<
  number,
  {
    getValue: (locale: LocaleIso) => number | string | undefined;
    service: CampaignServiceEndpoint;
  }
> = {
  215: {
    getValue: (locale: LocaleIso) => {
      if (isCH(locale)) return 25;
      if (isDK(locale)) return '20%';
      if (isSE(locale)) return '30%';

      return 15;
    },
    service: 'email.send.downloadThx'
  },
  392: {
    getValue: () => 15,
    service: 'email.send.downloadThx'
  },
  217: {
    getValue: () => undefined,
    service: 'email.send.downloadThx'
  },
  251: {
    getValue: () => undefined,
    service: 'email.send.downloadThxWithPrefCheck'
  }
};

/**
 * Campaign voucher selector for handling campaign-based voucher selection and personalization.
 *
 * This class encapsulates all business logic related to:
 * - Mapping pref strings to voucher variant names
 * - Mapping campaign IDs to email template IDs
 * - Selecting appropriate voucher variants from polymorphic array
 * - Determining which email service to use
 *
 * Based on Gatsby implementation:
 * - packages/core/cms/helpers/pref/voucher.js
 * - packages/core/cms/data-transformers/createPref.js
 *
 * @example
 * ```typescript
 * const campaignVoucher = new CampaignVoucher('de-DE');
 * const vouchers = [voucherGoogle, voucherBing, voucherOthers];
 *
 * // Select voucher based on URL campaign params
 * const campaignParams = { pref: 'cpc_gg_pid_...', campaignId: 58437274 };
 * const selectedVoucher = campaignVoucher.selectVoucherVariant(vouchers, campaignParams);
 *
 * // Get campaign details
 * const campaignDetails = campaignVoucher.getCampaignDetails(campaignParams);
 * // { variant: 'google', emailId: 215, serviceEndpoint: 'email.send.downloadThx', voucherValue: 15 }
 * ```
 */
export class CampaignVoucher {
  constructor(private readonly locale: LocaleIso) {}

  /**
   * Gets the voucher variant name from a pref string.
   * Extracts the base pref (first two underscore-separated parts) and looks up the variant.
   *
   * @param pref - The pref parameter from URL (e.g., 'cpc_gg_pid_kwd1882048492')
   * @returns The voucher variant name (google, bing, campaign_partner, or others)
   *
   * @example
   * ```typescript
   * getVoucherVariantByPref('cpc_gg_pid_...')      // → 'google'
   * getVoucherVariantByPref('cpc_bing_...')        // → 'bing'
   * getVoucherVariantByPref('cpl_apr_...')         // → 'campaign_partner'
   * getVoucherVariantByPref('unknown_prefix')      // → 'others'
   * getVoucherVariantByPref(undefined)             // → 'others'
   * ```
   */
  private getVoucherVariantByPref(pref?: string): VoucherVariant {
    if (!pref) return VOUCHER_VARIANTS.OTHERS;

    // Extract base pref (e.g., 'cpc_gg_pid_...' → 'cpc_gg')
    const basePref = pref.split('_').slice(0, 2).join('_');

    return CAMPAIGN_PREF_TO_VARIANT[basePref] || VOUCHER_VARIANTS.OTHERS;
  }

  /**
   * Looks up the email template ID for a given campaign ID.
   * Searches through all email template mappings to find which template this campaign belongs to.
   *
   * Priority: Check specific email IDs (392) before catch-all (215)
   * In Gatsby, only 215 and 392 are used for campaign mapping.
   *
   * @param campaignId - The campaign ID from URL parameter
   * @returns The email template ID if found, undefined otherwise
   *
   * @example
   * ```typescript
   * getEmailIdByCampaignId(58437274)    // → 215 (standard Google campaign)
   * getEmailIdByCampaignId(13918917553) // → 392 (specific campaign)
   * getEmailIdByCampaignId(99999999)    // → undefined (not in any mapping)
   * ```
   */
  private getEmailIdByCampaignId(campaignId?: number): number | undefined {
    if (!campaignId) return undefined;

    // Check specific campaigns first (392), then standard campaigns (215)
    for (const emailId of Object.keys(CAMPAIGN_EMAIL_ID_TO_CAMPAIGNS)) {
      const emailIdNum = Number(emailId);
      const campaigns = CAMPAIGN_EMAIL_ID_TO_CAMPAIGNS[emailIdNum];

      if (campaigns && campaigns.includes(campaignId)) {
        return emailIdNum;
      }
    }

    return undefined;
  }

  /**
   * Gets the email template ID for a given pref string.
   * Maps pref → variant → email ID.
   *
   * @param pref - The pref parameter from URL
   * @returns The email template ID if variant found, undefined otherwise
   *
   * @example
   * ```typescript
   * getEmailIdByPref('cpc_gg_...')        // → 215 (google → 215)
   * getEmailIdByPref('cpc_bing_...')      // → 215 (bing → 215)
   * getEmailIdByPref('cpl_apr_...')       // → 251 (campaign_partner → 251)
   * getEmailIdByPref(undefined)           // → undefined
   * ```
   */
  private getEmailIdByPref(pref?: string): number | undefined {
    const variant = this.getVoucherVariantByPref(pref);

    return variant ? CAMPAIGN_VARIANT_TO_EMAIL_ID[variant] : undefined;
  }

  /**
   * Gets the voucher value for a given email template ID.
   * Returns locale-specific values (e.g., CH gets 25, DK gets "20%").
   *
   * @param emailId - The email template ID
   * @returns The voucher value (number or percentage string), or undefined if no voucher
   *
   * @example
   * ```typescript
   * getVoucherValueByEmailId(215) // → 25 (for CH), 15 (for DE), "20%" (for DK)
   * getVoucherValueByEmailId(392) // → 15 (all locales)
   * getVoucherValueByEmailId(217) // → undefined (no voucher)
   * getVoucherValueByEmailId(251) // → undefined (no voucher)
   * ```
   */
  private getVoucherValueByEmailId(emailId?: number): number | string | undefined {
    if (!emailId) return undefined;

    const voucherConfig = CAMPAIGN_EMAIL_ID_TO_VOUCHER[emailId];

    return voucherConfig ? voucherConfig.getValue(this.locale) : undefined;
  }

  /**
   * Gets the voucher value for a given campaign ID.
   * Handles special case: Campaign 19761518826 in CH gets "25%" instead of 25.
   *
   * @param campaignId - The campaign ID from URL parameter
   * @returns The voucher value (number or percentage string), or undefined if no voucher
   *
   * @example
   * ```typescript
   * getVoucherValueByCampaignId(19761518826) // → "25%" (for CH locale)
   * getVoucherValueByCampaignId(58437274)    // → 25 (for CH), 15 (for DE)
   * getVoucherValueByCampaignId(13918917553) // → 15 (all locales)
   * ```
   */
  private getVoucherValueByCampaignId(campaignId?: number): number | string | undefined {
    if (!campaignId) return undefined;

    // Special case: Campaign 19761518826 in CH gets "25%" instead of 25
    if (campaignId === 19761518826 && isCH(this.locale)) {
      return '25%';
    }

    const emailId = this.getEmailIdByCampaignId(campaignId);

    return this.getVoucherValueByEmailId(emailId);
  }

  /**
   * Checks if a voucher matches the given variant name using substring matching.
   * Vouchers must have an id field for variant matching.
   *
   * Uses substring matching like Gatsby (e.g., 'voucher_google_variant'.includes('google')).
   *
   * @param voucher - Voucher object with id field
   * @param variantName - Variant name to match (e.g., 'google', 'bing', 'others')
   * @returns True if voucher matches the variant name
   *
   * @example
   * ```typescript
   * matchVoucherVariant({ id: 'voucher_google_variant' }, 'google')  // → true
   * matchVoucherVariant({ id: 'voucher_google_variant' }, 'bing')    // → false
   * ```
   */
  private matchVoucherVariant<T extends { id: string }>(voucher: T, variantName: string): boolean {
    return voucher.id.includes(variantName);
  }

  /**
   * Selects the appropriate voucher variant from an array based on campaign parameters.
   * Uses substring matching to find vouchers (e.g., 'voucher_google_variant'.includes('google')).
   *
   * Generic type constraint: T must have an 'id' string field
   * Works with VoucherNode, VoucherNewNode, or any object with an id
   *
   * Selection priority:
   * 1. If pref parameter provided → map to variant name → find matching voucher
   * 2. If no match found → caller should fall back to 'others' variant or first voucher
   *
   * @template T - Voucher type extending { id: string } (e.g., VoucherUnionNode from data-layer)
   * @param vouchers - Array of voucher objects (must have id field)
   * @param campaignParams - Campaign parameters from URL (pref, campaignId)
   * @returns The selected voucher, or undefined if not found
   *
   * @example
   * ```typescript
   * const vouchers = [
   *   { id: 'voucher_google_variant', ... },
   *   { id: 'voucher_bing_variant', ... },
   *   { id: 'voucher_others_variant', ... }
   * ];
   *
   * selectVoucherVariant(vouchers, { pref: 'cpc_gg_...' })  // → Returns google voucher
   * selectVoucherVariant(vouchers, { pref: 'cpc_bing_...' }) // → Returns bing voucher
   * selectVoucherVariant(vouchers, { pref: 'unknown' })     // → undefined (caller handles fallback)
   * ```
   */
  selectVoucherVariant<T extends { id: string }>(vouchers: T[], campaignParams: CampaignParams): T | undefined {
    const variantName = this.getVoucherVariantByPref(campaignParams.pref);

    return vouchers.find(voucher => this.matchVoucherVariant(voucher, variantName));
  }

  /**
   * Determines campaign voucher details based on campaign parameters.
   * Returns configuration for which PSI service endpoint to call, which email template to use,
   * and what voucher value to display.
   *
   * Selection priority:
   * 1. If campaignId provided → look up email ID from campaign mapping
   * 2. If pref provided (and no campaign match) → look up email ID from pref → variant mapping
   * 3. Otherwise → use default variant 'others' → email ID 217
   *
   * @param campaignParams - Campaign parameters from URL (pref, campaignId)
   * @returns Campaign voucher details including variant, emailId, service endpoint, and voucher value
   *
   * @example
   * ```typescript
   * // Campaign with specific email template and voucher
   * getCampaignDetails({ pref: 'cpc_gg_...', campaignId: 58437274 })
   * // → {
   * //   variant: 'google',
   * //   emailId: 215,
   * //   serviceEndpoint: 'email.send.downloadThx',
   * //   voucherValue: 15 (or 25 for CH, "20%" for DK, "30%" for SE)
   * // }
   *
   * // Special campaign in CH locale
   * getCampaignDetails({ campaignId: 19761518826 }) // locale: de-CH
   * // → {
   * //   variant: 'others',
   * //   emailId: 215,
   * //   serviceEndpoint: 'email.send.downloadThx',
   * //   voucherValue: "25%"
   * // }
   *
   * // Campaign partner variant (different service endpoint)
   * getCampaignDetails({ pref: 'cpl_apr_...' })
   * // → {
   * //   variant: 'campaign_partner',
   * //   emailId: 251,
   * //   serviceEndpoint: 'email.send.downloadThxWithPrefCheck',
   * //   voucherValue: undefined
   * // }
   *
   * // No pref or campaign (fallback to defaults)
   * getCampaignDetails({})
   * // → {
   * //   variant: 'others',
   * //   emailId: 217,
   * //   serviceEndpoint: 'email.send.downloadThx',
   * //   voucherValue: undefined
   * // }
   * ```
   */
  getCampaignDetails(campaignParams: CampaignParams): CampaignVoucherDetails {
    const variant = this.getVoucherVariantByPref(campaignParams.pref);

    // Determine email ID: campaignId takes priority, then pref, then default
    const emailId =
      this.getEmailIdByCampaignId(campaignParams.campaignId) || this.getEmailIdByPref(campaignParams.pref);

    // Get voucher value (handles special case for campaign 19761518826 in CH)
    const voucherValue =
      this.getVoucherValueByCampaignId(campaignParams.campaignId) || this.getVoucherValueByEmailId(emailId);

    // Get PSI service endpoint from email ID configuration
    const serviceEndpoint = emailId ? CAMPAIGN_EMAIL_ID_TO_VOUCHER[emailId]?.service : undefined;

    return {
      variant,
      emailId,
      serviceEndpoint,
      voucherValue
    };
  }
}
